import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { Button, Spin } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { getMarkerImage } from '../markers/markerImages';
import { GOOGLE_MAPS_API_KEY, streetViewLink } from '../../../utils/googleMaps';

// MapContainer ile aynı kütüphaneler (tek sefer yüklenir; sayfalar arası
// geometry çakışmasını önler).
const GMAP_LIBRARIES = ['places', 'geometry'];

const toNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

/**
 * Offsite ATM haritası.
 *
 * Etkileşimli Google haritası üzerinde seçili lokasyon ve çevredeki offsite
 * ATM'ler gerçek banka ikonlarıyla gösterilir. Bir ATM'ye (veya "Sokak
 * Görünümü" butonuna) tıklandığında o noktanın Street View panoraması alt
 * panelde açılır; kapsama yoksa Google Maps bağlantısına düşülür.
 */
const OffsiteMap = ({ location, atms = [], height = 340, streetViewHeight = 280 }) => {
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GMAP_LIBRARIES,
  });

  const centerLat = toNumber(location?.enlem);
  const centerLng = toNumber(location?.boylam);

  const points = useMemo(
    () =>
      (Array.isArray(atms) ? atms : [])
        .map((atm, index) => ({ ...atm, index, lat: toNumber(atm.latitude), lng: toNumber(atm.longitude) }))
        .filter((atm) => atm.lat != null && atm.lng != null),
    [atms]
  );

  // Street View maliyetli olduğu için otomatik yüklenmez; kullanıcı açar.
  const [svOpen, setSvOpen] = useState(false);
  const [svStatus, setSvStatus] = useState('idle'); // idle|loading|ok|none|error
  const [svTarget, setSvTarget] = useState(null);   // null = lokasyonun kendisi
  const panoRef = useRef(null);
  const panoInstanceRef = useRef(null);

  const hedef = useMemo(() => {
    if (centerLat == null || centerLng == null) return null;
    if (svTarget) return svTarget;
    return { lat: centerLat, lng: centerLng, etiket: location?.adi || 'Lokasyon' };
  }, [centerLat, centerLng, svTarget, location]);

  useEffect(() => {
    // SDK'yı useJsApiLoader yüklüyor; burada ikinci bir yükleyici çalıştırmıyoruz.
    if (!svOpen || !hedef || !isLoaded || !window.google?.maps) return undefined;
    const maps = window.google.maps;
    let iptal = false;
    setSvStatus('loading');

    new maps.StreetViewService().getPanorama(
      { location: { lat: hedef.lat, lng: hedef.lng }, radius: 80 },
      (data, status) => {
        if (iptal) return;
        if (status !== maps.StreetViewStatus.OK || !data?.location) {
          setSvStatus('none');
          return;
        }
        if (!panoRef.current) return;
        let heading = 0;
        try {
          heading = maps.geometry.spherical.computeHeading(
            data.location.latLng,
            new maps.LatLng(hedef.lat, hedef.lng)
          );
        } catch (e) {
          heading = 0;
        }
        panoInstanceRef.current = new maps.StreetViewPanorama(panoRef.current, {
          pano: data.location.pano,
          pov: { heading, pitch: 0 },
          zoom: 0,
          addressControl: false,
          motionTracking: false,
          motionTrackingControl: false,
          fullscreenControl: true,
          enableCloseButton: false,
        });
        setSvStatus('ok');
      }
    );

    return () => {
      iptal = true;
      panoInstanceRef.current = null;
    };
  }, [svOpen, hedef, isLoaded]);

  const acStreetView = useCallback((target) => {
    setSvTarget(target);
    setSvOpen(true);
  }, []);

  if (centerLat == null || centerLng == null) return null;
  const center = { lat: centerLat, lng: centerLng };
  const hedefEtiket = svTarget ? (svTarget.etiket || 'Offsite ATM') : (location?.adi || 'Seçili lokasyon');

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ width: '100%', height, border: '1px solid #d9d9d9', borderRadius: 10, overflow: 'hidden' }}>
        {isLoaded ? (
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '100%' }}
            center={center}
            zoom={16}
            options={{
              streetViewControl: false,
              mapTypeControl: false,
              fullscreenControl: true,
              gestureHandling: 'greedy',
            }}
          >
            <Marker
              position={center}
              title={location?.adi || 'Lokasyon'}
              zIndex={999}
              icon={{ url: 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png' }}
              onClick={() => acStreetView(null)}
            />
            {points.map((atm) => {
              const icon = getMarkerImage(atm.banka, 48, 48, 80);
              return (
                <Marker
                  key={`${atm.bankId || 'atm'}-${atm.lat}-${atm.lng}-${atm.index}`}
                  position={{ lat: atm.lat, lng: atm.lng }}
                  title={`${atm.banka || 'Offsite ATM'} · ${atm.mesafe_m ?? '-'} m\n${atm.adres || ''}\nSokak görünümü için tıklayın`}
                  icon={
                    icon
                      ? { url: icon, scaledSize: new window.google.maps.Size(30, 30), anchor: new window.google.maps.Point(15, 15) }
                      : undefined
                  }
                  onClick={() => acStreetView({ lat: atm.lat, lng: atm.lng, etiket: atm.banka || 'Offsite ATM' })}
                />
              );
            })}
          </GoogleMap>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#8c8c8c' }}>
            Harita yükleniyor…
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 18, alignItems: 'center', marginTop: 8, fontSize: 12, flexWrap: 'wrap' }}>
        <span><span style={{ color: '#1677ff', fontSize: 18 }}>●</span> Seçili lokasyon</span>
        <span>Offsite ATM ({points.length})</span>
        <Button
          size="small"
          icon={<EyeOutlined />}
          onClick={() => acStreetView(null)}
          type={svOpen && !svTarget ? 'primary' : 'default'}
        >
          Sokak Görünümü
        </Button>
        <span style={{ color: '#8c8c8c' }}>Banka ikonuna tıklayınca o noktanın sokak görünümü açılır.</span>
      </div>

      {svOpen && hedef && (
        <div style={{ marginTop: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, fontSize: 12 }}>
            <strong>Sokak Görünümü — {hedefEtiket}</strong>
            <Button size="small" type="text" onClick={() => setSvOpen(false)}>Kapat</Button>
          </div>
          <div
            ref={panoRef}
            style={{
              width: '100%',
              height: streetViewHeight,
              borderRadius: 10,
              overflow: 'hidden',
              border: '1px solid #d9d9d9',
              display: svStatus === 'ok' ? 'block' : 'none',
            }}
          />
          {svStatus === 'loading' && (
            <div style={{ height: streetViewHeight, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #d9d9d9', borderRadius: 10 }}>
              <Spin tip="Sokak görünümü yükleniyor..." />
            </div>
          )}
          {(svStatus === 'none' || svStatus === 'error') && (
            <div style={{ padding: 16, border: '1px solid #d9d9d9', borderRadius: 10, fontSize: 12, color: '#8c8c8c' }}>
              {svStatus === 'none'
                ? 'Bu konum için Street View görüntüsü bulunamadı.'
                : 'Sokak görünümü yüklenemedi.'}{' '}
              <a href={streetViewLink(hedef.lat, hedef.lng)} target="_blank" rel="noreferrer">
                Google Maps’te deneyin
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OffsiteMap;
